import { Box, Typography, useTheme, Paper, styled, Button, TableContainer, TableCell, TableRow, Table, TableHead, TableBody } from "@mui/material";
import jwtAxios from "../../utils/jwtAxios";
import { useState, useEffect } from "react";
import SubscriptionTable from "../subscriptions/SubscriptionTable";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { AppColor } from "../../types/Constants";
import { useAuthContext } from "../authentication/contexts/AuthContext";

function SubscriptionsIndex() {
    
    let theme = useTheme();
    let auth = useAuthContext();
    const [currentSubscription, setCurrentSubscription] = useState("");
    const [activeSubscription, setActiveSubscription] = useState("");
    const [subscriptionDate, setSubscriptionDate] = useState("");
    const [subscriptionAmount, setSubscriptionAmount] = useState("");
    const [subscriptionCancel, setSubscriptionCancel] = useState(false);
    const [pricingTiers, setPricingTiers] = useState([]);
    const [firstTimeUser, setFirstTimeUser] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const tiers = ["tier_starter", "tier_pro", "tier_enterprise"];

    const Item = styled(Paper)(({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
        ...theme.typography.body1,
        padding: theme.spacing(3),
        textAlign: 'center',
      }));

      useEffect(() => {
        // Fetch current subscription from the server or any other source
        const fetchCurrentSubscription = () => {
            setIsLoading(true);
            jwtAxios.get("api/subscription/current")
                .then(response => {
                    var status = response.data.status;
                    var renewalDate = response.data.renewal_date;
                    var stringAmount = response.data.amount;
                    var amount = stringAmount.slice(0, stringAmount.length-2) + '.' + stringAmount.slice(-2);
                    var cancel = response.data.cancel_at_period_end;
                    var pricingTiers = response.data.pricing_tiers;
                    var currentTier = response.data.current_tier;

                    if (tiers.some(tier => currentTier == tier)) {
                        setCurrentSubscription(currentTier);
                    } else {

                        const tier = pricingTiers.find((tier: { name: string, price: number, id: string }) => tier.id === currentTier);

                        if (tier) {
                            const tierNameParted = tier.name.split(' ');
                            const tierName = `tier_${tierNameParted[1].toLowerCase()}`;
                            setCurrentSubscription(tierName);
                        }

                    }

                    setSubscriptionAmount(amount);
                    setSubscriptionCancel(cancel);
                    setActiveSubscription(status.charAt(0).toUpperCase() + status.slice(1));
                    
                    const date = new Date(renewalDate);
                    setSubscriptionDate(date.toLocaleDateString());
                    
                    // Get pricing tiers and stripeid
                    setPricingTiers(pricingTiers);
                    if (response.data.stripe_id == null) {
                        setFirstTimeUser(true);
                    }

                    setIsLoading(false);
                })
                .catch(error => {
                    console.error('Error fetching current subscription:', error);
                });
        };
        
        fetchCurrentSubscription();
        
        
    },[]);

    const cancelSubscription = () => {
        const apiUrl = "api/subscription/cancel";

        jwtAxios.post(apiUrl)
            .then(response => {
                window.location.href = response.data.url;
            })
            .catch(error => {
                console.error('There was a problem with the API request:', error);
            });
    };

    const manageSubscription = () => {
        const apiUrl = "api/subscription/renew";

        jwtAxios.post(apiUrl)
            .then(response => {
                window.location.href = response.data.url;
            })
            .catch(error => {
                console.error('There was a problem with the API request:', error);
            });
    };

    const handleTrial = () => {

        const apiUrl = "api/subscription/trial";

        jwtAxios.post(apiUrl)
            .then(response => {
                window.location.href = response.data.url;
            })
            .catch(error => {
                console.error('There was a problem with the API request:', error);
            });
    };

    const displaySubscription = (currentSubscription: string) => {
        switch (currentSubscription) {
            case 'tier_starter':
              return 'Starter';
            case 'tier_professional':
              return 'Professional';
            case 'tier_enterprise':
              return 'Enterprise';
            case 'tier_pro':
                return 'Professional';
            default:
              return '';
          }
    };

    const renderHeader = () => {
        return (
        <Box justifyContent="center">
            <Box padding={theme.spacing(8)}>
                <Typography variant="h4" align="center" mb={4} fontWeight="600">Subscription</Typography>
                <Typography variant="subtitle1" textAlign="center" maxWidth="50%" margin="auto"> Here is your current subscription to Skidattl. You can choose to upgrade your current subscription to gain access to more features and benefits.</Typography>
            </Box>
        </Box>
        );
    };

    if(isLoading){
    return (
    <Box height="100">
        {renderHeader()}
    <Skeleton highlightColor={AppColor.skeletonHighlight} baseColor={AppColor.skeletonBase} style={{ margin: "auto", display: "flex", lineHeight: 2, width: "70%", }} count={2} />
    {/* Temporarily comment this out for fastsigns convention */}
    {/* <SubscriptionTable currentSubscription={""} pricingTiers={pricingTiers} dashboardRequired={false} loading={true}/> */}
    </Box>
    
    );
    } else if (auth.isBusinessUser()) {
    return (
    <Box height="100">
    {renderHeader()}
    
    <TableContainer component={Item} sx={{ margin: "auto", marginBottom: "20px", width: "70%"}}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Current Subscription</TableCell>
            <TableCell align="left">Status</TableCell>
            {subscriptionCancel ? ( 
                <TableCell align="left">Subscription End Date</TableCell>
            ) : (<TableCell align="left">Next Payment</TableCell> 
            )}
            <TableCell align="left">Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
            <TableRow>
              <TableCell align="left" width="30%">{displaySubscription(currentSubscription)}</TableCell>
              {subscriptionCancel ? (
                    <TableCell align="left">Pending Cancellation</TableCell>
                ) : (
                    <TableCell align="left"><Box sx={activeSubscription == "Active" ? {backgroundColor: "#68A200" + 44, color: "#68A200", display: "flex", borderRadius: "5px", justifyContent: "center", width: "70%"} : {}}>{activeSubscription}</Box></TableCell>
                )}
              <TableCell align="left">{subscriptionDate}</TableCell>
              <TableCell align="left">${subscriptionAmount}</TableCell>
            </TableRow>
        </TableBody>
      </Table>
    </TableContainer>

    {/* <SubscriptionTable currentSubscription={currentSubscription} pricingTiers={pricingTiers} dashboardRequired={true}/> */}

    <Box mt={10} mb={10}>
        {!subscriptionCancel ? (
            <Item sx={{width: "75%", textAlign: "left", margin: "auto"}}>
                <Typography mb={4} variant="h5">Cancel Subscription</Typography>
                <Typography mb={4} variant="body1">Your subscription will cancel at the billing period end.</Typography>
                <Button variant="contained" onClick={() => {cancelSubscription();}}>Cancel</Button>
            </Item>
        ) : (
            <Item sx={{width: "75%", textAlign: "left", margin: "auto"}}>
            <Typography mb={4} variant="h5">Renew Subscription</Typography>
            <Typography mb={4} variant="body1">If you would like to renew your subscription.</Typography>
            <Button variant="contained" onClick={() => {manageSubscription();}}>Renew</Button>
        </Item>
        )}
        
    </Box>
    </Box>
    );
  } else {
    return (
        <>
        <Box padding={theme.spacing(8)}>
            <Typography variant="h4" align="center" mb={4} fontWeight="600">Choose Your Subscription</Typography>
            <Typography variant="subtitle1" textAlign="center" maxWidth="50%" margin="auto"> A subscription is required to access features of Skidattl.</Typography>
            {firstTimeUser && (
                <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
                    <Button variant="contained" sx={{ display: "flex", justifyContent: "center" }} onClick={handleTrial}>
                        Start a Free Trial <KeyboardArrowRightIcon />
                    </Button>
                </Box>
            )}
        </Box>
        <SubscriptionTable currentSubscription={""} pricingTiers={pricingTiers} dashboardRequired={false}/>
        </>
    );
  }
}
  
  export default SubscriptionsIndex;